import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Building2, User, Mail, Phone, MapPin, Lock, Eye, EyeOff,
    ArrowLeft, ArrowRight, CheckCircle, Loader2, Store
} from 'lucide-react';
import OpenStreetMapAddressPicker from '../../components/ui/GoogleMapsAddressPicker';
import { sanitizePhoneInput } from '@/utils/phoneUtils';
import { useAlert } from '@/components/AlertProvider';

type Step = 1 | 2 | 3;

const ClientRegister: React.FC = () => {
    const { showSuccess, showError } = useAlert();
    const [currentStep, setCurrentStep] = useState<Step>(1);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [form, setForm] = useState({
        // Step 1 - Company Info
        company_name: '',
        first_name: '',
        last_name: '',
        client_type: 'pharmacie' as 'pharmacie' | 'parapharmacie' | 'centrale' | 'magasin_bio' | 'therapeute',
        siret: '',
        // Step 2 - Contact & Address
        email: '',
        invoice_email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'France',
        maps_link: '',
        // Step 3 - Password
        password: '',
        confirm_password: '',
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    const validateStep = (step: Step): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 1) {
            if (!form.company_name.trim()) {
                newErrors.company_name = 'Le nom de l\'entreprise est requis';
            }
            if (!form.client_type) {
                newErrors.client_type = 'Le type d\'établissement est requis';
            }
        }

        if (step === 2) {
            if (!form.email.trim()) {
                newErrors.email = 'L\'email est requis';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
                newErrors.email = 'L\'email n\'est pas valide';
            }
        }

        if (step === 3) {
            if (!form.password) {
                newErrors.password = 'Le mot de passe est requis';
            } else if (form.password.length < 6) {
                newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
            }
            if (form.password !== form.confirm_password) {
                newErrors.confirm_password = 'Les mots de passe ne correspondent pas';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nextStep = () => {
        if (validateStep(currentStep)) {
            setCurrentStep((prev) => Math.min(prev + 1, 3) as Step);
        }
    };

    const prevStep = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1) as Step);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateStep(3)) return;

        setLoading(true);

        try {
            const response = await fetch('/api/v1/clients/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    company_name: form.company_name,
                    first_name: form.first_name || undefined,
                    last_name: form.last_name || undefined,
                    email: form.email,
                    phone: form.phone || undefined,
                    address: form.address || undefined,
                    city: form.city || undefined,
                    postal_code: form.postal_code || undefined,
                    country: form.country || 'France',
                    maps_link: form.maps_link || undefined,
                    siret: form.siret || undefined,
                    password: form.password,
                    is_active: true,
                    client_type: form.client_type,
                    invoice_email: form.invoice_email || undefined,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Erreur lors de l\'inscription');
            }

            setSuccess(true);
            showSuccess('Inscription réussie !');
        } catch (err) {
            showError(err instanceof Error ? err.message : 'Erreur lors de l\'inscription');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-green-50/20 to-slate-50 dark:from-slate-950 dark:via-green-950/10 dark:to-slate-950 p-4">
                <div className="max-w-md w-full">
                    <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-xl p-8">
                        <div className="text-center">
                            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
                            </div>

                            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                                Inscription réussie !
                            </h2>

                            <p className="text-slate-600 dark:text-slate-400 mb-6">
                                Votre compte a été créé avec succès. Un email de validation vient de vous être envoyé.
                            </p>

                            <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-6">
                                <p className="text-sm text-orange-800 dark:text-orange-300">
                                    ⚠️ Votre compte <strong>{form.email}</strong> est en attente de validation. Veuillez vérifier vos emails pour activer votre accès.
                                </p>
                            </div>

                            <Link
                                to="/login"
                                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 font-medium"
                            >
                                Se connecter
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-green-50/20 to-slate-50 dark:from-slate-950 dark:via-green-950/10 dark:to-slate-950">
            {/* Left Side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-gray-200 items-center justify-center">
                <div className="absolute inset-0 bg-[#fff7ec]"></div>
                <div className="relative z-10 flex flex-col items-center p-12 text-center">
                    <img
                        src="/NOVA - Goutte ALLIANCE.png"
                        alt="Nova logo"
                        className="w-64 h-64 object-contain mb-8"
                    />
                    <h1 className="text-4xl font-bold mb-3 font-josefin tracking-[0.2em] text-lime-800">NOVA</h1>
                    <p className="text-lg text-gray-600 max-w-md">
                        Rejoignez notre plateforme de gestion
                    </p>
                </div>
                <div className="absolute top-10 right-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute bottom-10 left-10 w-32 h-32 bg-purple-400/20 rounded-full blur-2xl"></div>
            </div>

            {/* Right Side - Registration Form */}
            <div className="flex-1 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 overflow-auto">
                <div className="max-w-xl w-full space-y-6">
                    {/* Mobile Logo */}
                    <div className="text-center lg:hidden">
                        <img
                            src="/NOVA - Goutte ALLIANCE.png"
                            alt="Nova logo"
                            className="w-16 h-16 object-contain mx-auto mb-3 rounded-xl"
                        />
                        <h1 className="text-xl font-bold text-emerald-700 dark:text-emerald-400 font-josefin tracking-[0.3em]">NOVA</h1>
                    </div>

                    {/* Progress Steps */}
                    <div className="flex items-center justify-center gap-2 mb-6">
                        {[1, 2, 3].map((step) => (
                            <React.Fragment key={step}>
                                <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${currentStep >= step
                                        ? 'bg-green-600 text-white shadow-lg'
                                        : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                        }`}
                                >
                                    {currentStep > step ? <CheckCircle className="h-5 w-5" /> : step}
                                </div>
                                {step < 3 && (
                                    <div
                                        className={`w-12 h-1 rounded-full transition-all duration-300 ${currentStep > step ? 'bg-green-600' : 'bg-slate-200 dark:bg-slate-700'
                                            }`}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 shadow-xl p-6 sm:p-8">
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                                {currentStep === 1 && 'Informations Entreprise'}
                                {currentStep === 2 && 'Contact & Adresse'}
                                {currentStep === 3 && 'Créer votre mot de passe'}
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                Étape {currentStep} sur 3
                            </p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {/* Step 1 - Company Info */}
                            {currentStep === 1 && (
                                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            <Building2 className="inline h-4 w-4 mr-1" />
                                            Nom de l'entreprise *
                                        </label>
                                        <input
                                            type="text"
                                            value={form.company_name}
                                            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                            className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${errors.company_name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                                                }`}
                                            placeholder="Nom de votre entreprise"
                                        />
                                        {errors.company_name && (
                                            <p className="text-red-500 text-xs mt-1">{errors.company_name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            <Store className="inline h-4 w-4 mr-1" />
                                            Type d'établissement *
                                        </label>
                                        <select
                                            value={form.client_type}
                                            onChange={(e) => setForm({ ...form, client_type: e.target.value as typeof form.client_type })}
                                            className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${errors.client_type ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                                                }`}
                                        >
                                            <option value="pharmacie">Pharmacie</option>
                                            <option value="parapharmacie">Parapharmacie</option>
                                            <option value="centrale">Centrale d'achat</option>
                                            <option value="magasin_bio">Magasin Bio</option>
                                            <option value="therapeute">Thérapeute</option>
                                        </select>
                                        {errors.client_type && (
                                            <p className="text-red-500 text-xs mt-1">{errors.client_type}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            <User className="inline h-4 w-4 mr-1" />
                                            Nom du contact
                                        </label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <input
                                                type="text"
                                                value={form.first_name}
                                                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                                                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                                                placeholder="Prénom"
                                            />
                                            <input
                                                type="text"
                                                value={form.last_name}
                                                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                                                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                                                placeholder="Nom"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                SIRET
                                            </label>
                                            <input
                                                type="text"
                                                value={form.siret}
                                                onChange={(e) => setForm({ ...form, siret: e.target.value })}
                                                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                                                placeholder="123 456 789 00012"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Step 2 - Contact & Address */}
                            {currentStep === 2 && (
                                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                <Mail className="inline h-4 w-4 mr-1" />
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${errors.email ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                                                    }`}
                                                placeholder="votre@email.com"
                                            />
                                            {errors.email && (
                                                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                <Phone className="inline h-4 w-4 mr-1" />
                                                Téléphone
                                            </label>
                                            <input
                                                type="tel"
                                                value={form.phone}
                                                onChange={(e) => setForm({ ...form, phone: sanitizePhoneInput(e.target.value) })}
                                                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                                                placeholder="+33 1 23 45 67 89"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            <Mail className="inline h-4 w-4 mr-1" />
                                            Email de facturation
                                        </label>
                                        <input
                                            type="email"
                                            value={form.invoice_email}
                                            onChange={(e) => setForm({ ...form, invoice_email: e.target.value })}
                                            className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                                            placeholder="facturation@email.com (optionnel)"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">Laissez vide pour utiliser l'email principal</p>
                                    </div>

                                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            <MapPin className="inline h-4 w-4 mr-1" />
                                            Adresse
                                        </label>
                                        <OpenStreetMapAddressPicker
                                            onAddressSelect={(addressData: any) => {
                                                setForm({
                                                    ...form,
                                                    address: addressData.address,
                                                    city: addressData.city,
                                                    postal_code: addressData.postal_code,
                                                    country: addressData.country,
                                                    maps_link: addressData.maps_link
                                                });
                                            }}
                                            initialAddress={form.address}
                                            initialCity={form.city}
                                            initialPostalCode={form.postal_code}
                                            initialCountry={form.country}
                                            initialMapsLink={form.maps_link}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Step 3 - Password */}
                            {currentStep === 3 && (
                                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            <Lock className="inline h-4 w-4 mr-1" />
                                            Mot de passe *
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                value={form.password}
                                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                                className={`w-full px-4 py-3 pr-12 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${errors.password ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                                                    }`}
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                            </button>
                                        </div>
                                        {errors.password && (
                                            <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                                        )}
                                        <p className="text-xs text-slate-500 mt-1">Minimum 6 caractères</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            <Lock className="inline h-4 w-4 mr-1" />
                                            Confirmer le mot de passe *
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={form.confirm_password}
                                                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                                                className={`w-full px-4 py-3 pr-12 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${errors.confirm_password ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                                                    }`}
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                            </button>
                                        </div>
                                        {errors.confirm_password && (
                                            <p className="text-red-500 text-xs mt-1">{errors.confirm_password}</p>
                                        )}
                                    </div>

                                    <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                                        <p className="text-sm text-orange-800 dark:text-orange-300">
                                            ℹ️ Un email de validation vous sera envoyé pour activer votre compte.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Navigation Buttons */}
                            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                                {currentStep > 1 ? (
                                    <button
                                        type="button"
                                        onClick={prevStep}
                                        className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                                    >
                                        <ArrowLeft className="h-4 w-4" />
                                        Précédent
                                    </button>
                                ) : (
                                    <Link
                                        to="/login"
                                        className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                                    >
                                        <ArrowLeft className="h-4 w-4" />
                                        Connexion
                                    </Link>
                                )}

                                {currentStep < 3 ? (
                                    <button
                                        type="button"
                                        onClick={nextStep}
                                        className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 font-medium"
                                    >
                                        Suivant
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors duration-200 font-medium"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Inscription...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="h-4 w-4" />
                                                S'inscrire
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    {/* Footer */}
                    <div className="text-center">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            Vous avez déjà un compte ?{' '}
                            <Link to="/login" className="text-green-600 hover:text-green-700 font-medium">
                                Connectez-vous
                            </Link>
                        </p>
                    </div>

                    {/* Powered by Digitalia */}
                    <div className="flex flex-col items-center space-y-2">
                        <img
                            src="/digit.png"
                            alt="Digitalia"
                            className="h-6 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-200"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Powered by <span className="font-semibold">Digitalia</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientRegister;
